/**
 * Every task definition, as DATA (ADR-001 constraint C5).
 *
 * Twenty-five activities in twenty-four families. Four of them — arithmetic, sudoku, the alphabet
 * and finding a letter in a word — are the tasks a teacher would recognise as *lessons* rather than
 * as games with a hidden purpose (ADR-011), and they are also the four with script on the canvas,
 * which is not a coincidence: a lesson in a symbol system has to show the symbols.
 *
 * **On the difficulty ceilings.** Every ladder was extended, because an objective is a band of
 * levels inside a game and a game with five levels can carry two objectives at most. But they
 * were not extended uniformly, and the reason is physical: some games grow by adding cells and
 * some cannot, because a cell below roughly 76 px is a target a five-year-old's finger misses,
 * and a mis-aimed tap recorded as a wrong answer is a motor artefact scored as not knowing the
 * answer. Where the spatial axis ran out, difficulty grows through subtlety instead — the same
 * shapes in different colours, positions swapped rather than changed, gaps between sizes
 * narrowed. Each file says which axis it uses and why.
 *
 * `stepDownAfter` and `maxTrials` are on every task. There is no mode flag: see the note in
 * `@talent/protocol`'s `tasks.ts` for why one was written and removed.
 */
/**
 * The shared teaching policy.
 *
 * Two consecutive errors drop a level, and a sitting is twelve trials. Twelve because a
 * preschool activity that outlasts about five minutes is one the child leaves rather than
 * finishes, and because the ladder needs enough trials to actually move: at `stepUpAfter: 2`
 * and twelve trials a child can climb five levels in one sitting, which is what makes the
 * level a teacher reads mean something after a single afternoon.
 */
const TEACH = { stepDownAfter: 2, maxTrials: 12 };
// ---------------------------------------------------------------------------------------
// Number
// ---------------------------------------------------------------------------------------
/**
 * Arithmetic — count, then add, then take away, then group.
 *
 * The one activity a preschool teacher would ask for by name, and the one that broke the
 * oldest rule in this codebase: **there are numerals on the screen.** Everything before it was
 * text-free and language-independent by policy (v2 §15), because reading ability must not leak
 * into a measure of something else. That policy is intact where it applies and inapplicable
 * here — the numeral IS the thing being taught, so refusing to draw one would mean refusing to
 * teach arithmetic (R-6).
 *
 * The first four levels and the multiplication levels still show groups of objects rather than
 * digits, and that is the actual curriculum rather than a concession to the old rule: a child
 * counts things before they read the symbol for how many, and multiplication is equal groups
 * before it is a times table.
 *
 * **Multiplication and division sit at the top on purpose.** A five-year-old will not reach
 * them, and that is the design: the ladder starts where a preschooler starts and keeps going
 * for the seven-year-old in the same class. Nobody is pushed there — the recommender only
 * offers an objective whose predecessors are done.
 */
export const mathArith = {
    id: "math-arith-bare",
    version: "1.1.0",
    construct: "numeracy",
    family: "math-arith",
    presentation: {
        narrative: false,
        theme: "plain-shapes",
        locale: "fa-IR",
        /**
         * False, and it is the only task in the bundle that declares it.
         *
         * Persian-Indic digits are script. A child who cannot yet read ۷ is at a disadvantage on
         * the digit levels that has nothing to do with whether they can add — which is exactly the
         * confound the flag exists to mark, and marking it is what stops the number being read as
         * an arithmetic result later.
         */
        languageIndependentStimulus: false,
    },
    measurement: {
        scoring: "accuracy",
        /**
         * Two, because the counting levels stopped drawing and started walking.
         *
         * They used to serve a shuffled permutation of the range; they now go one, two, three, in
         * order, every time (`math.ts`). That is a change to what a level ASKS rather than to how it
         * looks, so a sitting recorded under calibration 1 and one recorded under 2 are not the same
         * quantity — the first sampled the range, the second walks the front of it — and pooling them
         * would report a number describing neither. This is exactly what the field is for (v2 §18).
         */
        calibrationVersion: "2",
        formIds: ["A", "B", "C"],
        minDifficulty: 1,
        maxDifficulty: 16,
        stepUpAfter: 3,
        ...TEACH,
        // Unhurried. Arithmetic under a clock measures fluency, which is not what is being taught
        // here, and PC-1 keeps every timing signal out of scoring anyway.
        trialTimeoutMs: 60_000,
    },
    params: {},
};
// ---------------------------------------------------------------------------------------
// Letters
// ---------------------------------------------------------------------------------------
/**
 * The alphabet — which letter is this, and is it still that letter when it changes shape?
 *
 * The other half of a preschool week. Arithmetic was the one activity a teacher would ask for
 * by name; letters is the other, and the bundle had nothing for it until now.
 *
 * **The third task with script on the canvas, and the last one that can claim the exemption.**
 * The rule was that a stimulus is text-free so reading ability cannot leak into a measure of
 * something else (v2 §15). Arithmetic broke it because the numeral IS the thing taught (R-6);
 * sudoku's digit levels inherited the same argument. This one is the purest case of it: the
 * letter is not a confound here, it is the entire subject. Refusing to draw ب would mean
 * refusing to teach the alphabet.
 *
 * What the exemption does NOT license is a sentence. Nothing in this game reads a word, and
 * `letters.ts` produces one shaped letter at a time through a branded type that `glyph.ts` is
 * the only consumer of — so the door that was opened is exactly one letter wide.
 *
 * **Difficulty grows through confusability, never through more cards.** Four cards throughout
 * and they never shrink, because this is a game about looking closely at one thing and a card
 * under roughly 76 px is a target a five-year-old's finger misses. What changes is how alike
 * the four are, and then whether they are wearing the same shape as the letter above them.
 */
export const litLetters = {
    id: "lit-letters-bare",
    version: "1.0.0",
    construct: "literacy",
    family: "lit-letters",
    presentation: {
        narrative: false,
        theme: "plain-letters",
        locale: "fa-IR",
        /**
         * False, and it is the most obviously false of the three that declare it.
         *
         * Marking it is what stops a score here being read later as anything other than what it
         * is: how well a child tells Persian letterforms apart. That is a script-bound skill by
         * construction, and a number describing it must never be pooled with the games that
         * deliberately need no reading at all.
         */
        languageIndependentStimulus: false,
    },
    measurement: {
        scoring: "accuracy",
        calibrationVersion: "1",
        /**
         * Six forms, and the number is arithmetic rather than taste.
         *
         * A form does two jobs here. The usual one is controlling practice effects — a child who
         * meets the same four cards every time learns the row rather than the letter. The second is
         * particular to this game: the form decides where in the alphabet a sitting STARTS, because
         * the trial counter restarts at zero and without an offset every sitting would begin at ا.
         *
         * `formForToday` rotates the form by the day, a sitting is twelve trials at two trials to a
         * letter, and thirty-two letters divided by six letters a sitting needs six starting points.
         * So six forms is exactly what it takes for a child playing through a week to have been round
         * the whole alphabet. Three left half of it unreachable.
         */
        formIds: ["A", "B", "C", "D", "E", "F"],
        minDifficulty: 1,
        maxDifficulty: 12,
        stepUpAfter: 3,
        ...TEACH,
        // Comparing four letterforms is looking, not working. Generous, and PC-1 means nothing
        // here is a speed measure in any case.
        trialTimeoutMs: 45_000,
    },
    params: {},
};
/**
 * Which word is this letter hiding in?
 *
 * The step after the alphabet game. That one teaches what a letter looks like, including the
 * shapes it wears inside a word; this one asks the child to go and find one — which is the
 * skill that turns letter knowledge into reading.
 *
 * **A pre-reader can play it.** Nothing asks what a word says. The child matches a shape
 * against four small pictures made of shapes, which is what reading is before it is reading.
 *
 * **The fourth task with script on the canvas, and the one that widened the exemption from a
 * letter to a word.** The other three draw one symbol at a time. This one cannot: "find the
 * letter in a word" has no version without a word. The vocabulary is closed and versioned with
 * the bundle — eighty-odd nouns a preschool classroom already says out loud — so what reaches
 * the canvas is still content this repository chose, not free text.
 *
 * Difficulty grows through how alike the wrong words are, then through where the letter hides.
 * The cards never shrink: a word is drawn small enough to read and the whole card is the target,
 * which is what makes this buildable at all where tapping a letter inside a sentence was not.
 */
export const litFindLetter = {
    id: "lit-find-letter-bare",
    version: "1.0.0",
    construct: "literacy",
    family: "lit-find-letter",
    presentation: {
        narrative: false,
        theme: "plain-letters",
        locale: "fa-IR",
        /** Words. The most script-bound stimulus in the bundle, and the flag says so. */
        languageIndependentStimulus: false,
    },
    measurement: {
        scoring: "accuracy",
        calibrationVersion: "1",
        // Six, for the reason spelled out on the alphabet game: the form is also where in the
        // alphabet a sitting starts, and six starting points is what covers thirty-two letters.
        formIds: ["A", "B", "C", "D", "E", "F"],
        minDifficulty: 1,
        maxDifficulty: 10,
        stepUpAfter: 3,
        ...TEACH,
        // Searching four words is looking, not working, and PC-1 means nothing here is timed.
        trialTimeoutMs: 45_000,
    },
    params: {},
};
// ---------------------------------------------------------------------------------------
// Pattern
// ---------------------------------------------------------------------------------------
/**
 * Lost world — the pattern repeats, so what comes next?
 *
 * The oldest reasoning task there is, and the one that needs no explanation: a repetition is
 * visible without being described.
 *
 * Extended from five levels to ten, and the new ones are a different KIND of pattern rather
 * than more of the same. Levels 1–5 repeat a fixed unit (ABAB, ABCABC); 6–8 repeat units with
 * an internal shape (ABB, AABB), which is what a preschool curriculum actually calls a pattern
 * and which the first version could not express; 9–10 grow — each repetition is one longer
 * than the last — which is the first level where the rule is about change rather than sameness.
 *
 * This is where "pattern recognition" as a request lands. It was already two games (matching a
 * pattern, and completing one); what it needed was depth, not a third game doing the same job.
 */
export const vsSequence = {
    id: "vs-sequence-bare",
    version: "2.0.0",
    construct: "reasoning",
    family: "vs-sequence",
    presentation: {
        narrative: false,
        theme: "plain-shapes",
        locale: "fa-IR",
        languageIndependentStimulus: true,
    },
    measurement: {
        scoring: "accuracy",
        calibrationVersion: "2",
        formIds: ["A", "B"],
        minDifficulty: 1,
        maxDifficulty: 10,
        stepUpAfter: 3,
        ...TEACH,
        trialTimeoutMs: 45_000,
    },
    params: {},
};
/**
 * The colour world — the same strip, told in colour instead of shape.
 *
 * A separate family rather than a lower level of the one above, because it is a different question
 * and a preschooler can answer it a year earlier. Reading `circle, square, circle` means holding
 * three names; reading `red, blue, red` means holding nothing — the eye does it. That makes this the
 * gentlest entry into patterns the product has, and the one a four-year-old starts on.
 *
 * Eight levels rather than ten: with four colours there is no equivalent of a six-shape alphabet, and
 * padding the top with longer strips would make it tedious rather than harder.
 *
 * Shares the generator and the renderer with `vs-sequence` — the only difference is a flag on the
 * recipe. Two implementations of "what continues this pattern" is one implementation and one thing
 * that disagrees with it.
 */
export const vsSequenceColor = {
    id: "vs-sequence-color-bare",
    version: "1.0.0",
    construct: "reasoning",
    family: "vs-sequence-color",
    presentation: {
        narrative: false,
        theme: "plain-shapes",
        locale: "fa-IR",
        languageIndependentStimulus: true,
    },
    measurement: {
        scoring: "accuracy",
        calibrationVersion: "1",
        formIds: ["A", "B"],
        minDifficulty: 1,
        maxDifficulty: 8,
        stepUpAfter: 3,
        ...TEACH,
        trialTimeoutMs: 45_000,
    },
    params: {},
};
/**
 * Carry the pattern on — a motif at the start of a strip, and the rest of it to fill.
 *
 * **A different question from `vs-sequence`, and the difference is a dimension.** That game reads a
 * row left to right and is answered with one card; this one prints a small BLOCK — three rows tall
 * — and asks the child to stamp it again along the strip. A child who can finish
 * `red, blue, red, blue` often cannot carry a three-row motif, because the second needs the unit
 * seen as a unit rather than as a run of items.
 *
 * **The child paints rather than picks, and that is what makes a level legible.** Where the strip
 * breaks says what they were doing: right for the first column and drifting afterwards is the idea
 * held and the place lost; every cell one colour is the unit never seen at all. A multiple-choice
 * version records neither, and this is the only pattern game in the bundle that can tell them apart.
 *
 * Three rows always, because four puts a cell under the size a five-year-old's finger can hit, and
 * the strip stops at nine columns — past that a trial becomes a scrolling exercise rather than a
 * pattern one. Difficulty is the motif: wider, then more colours, then more of it filled.
 */
export const vsTiling = {
    id: "vs-tiling-bare",
    version: "2.0.0",
    construct: "reasoning",
    family: "vs-tiling",
    presentation: {
        narrative: false,
        theme: "plain-shapes",
        locale: "fa-IR",
        languageIndependentStimulus: true,
    },
    measurement: {
        scoring: "accuracy",
        /**
         * Two, because the motif changed shape and the strip changed length.
         *
         * The first level used to be a one-column motif — a bar rather than a picture — and the ladder
         * ran three blocks throughout. Every level is now at least two columns wide and the lower half
         * is stamped once rather than twice, so a level number names a different trial than it did and
         * the two must not be pooled (v2 §18).
         */
        calibrationVersion: "2",
        formIds: ["A", "B"],
        minDifficulty: 1,
        maxDifficulty: 8,
        stepUpAfter: 2,
        ...TEACH,
        // Filling a strip is unhurried, and a child checking the motif before every cell is doing the
        // task properly rather than slowly (PC-1).
        trialTimeoutMs: 180_000,
    },
    params: {},
};
/**
 * Match the pattern — which of these is the same as the one above?
 *
 * Difficulty is the number of filled cells: more cells make the near-miss distractors subtler.
 * The ceiling is eight because the grid is 3×3 and nine filled cells is a solid square, which
 * has no near miss. The grid does not grow: four columns on a 360 px phone puts each option
 * tile's cells under the floor, and this is a game of comparing small marks.
 */
export const vsPatternMatch = {
    id: "vs-pattern-match-bare",
    version: "2.0.0",
    construct: "visuospatial",
    family: "vs-pattern-match",
    presentation: {
        narrative: false,
        theme: "plain-shapes",
        locale: "fa-IR",
        languageIndependentStimulus: true,
    },
    measurement: {
        scoring: "accuracy",
        calibrationVersion: "1",
        formIds: ["A", "B"],
        minDifficulty: 1,
        maxDifficulty: 8,
        stepUpAfter: 3,
        ...TEACH,
        trialTimeoutMs: 25_000,
    },
    params: {
        gridCols: 3,
        gridRows: 3,
        distractors: 3,
        rotationAllowed: false,
    },
};
/**
 * Sudoku — every row, every column, every box, one of each.
 *
 * Requested by name, and the only genuinely new *kind* of thinking in the bundle: every other
 * game asks the child to find something, and this one asks them to rule things out. A
 * four-by-four with two blanks is well within reach of a five-year-old who can match shapes,
 * which is why it starts there and not at nine-by-nine.
 *
 * **Shapes first, digits later.** Levels 1–6 are shape sudoku, so the puzzle is pure
 * elimination with nothing to read. Levels 7–10 swap in Persian digits, which is the same
 * puzzle plus symbol recognition — a second thing to learn, and therefore its own objective
 * rather than a silent change at level 7.
 *
 * The ceiling is a six-by-six. A nine-by-nine on a phone gives cells around 38 px, and it is
 * not a preschool activity in any case: a full 9×9 takes an adult ten minutes.
 */
export const logicSudoku = {
    id: "logic-sudoku-bare",
    version: "1.1.0",
    construct: "reasoning",
    family: "logic-sudoku",
    presentation: {
        narrative: false,
        theme: "plain-shapes",
        locale: "fa-IR",
        // The digit levels are marked the same way arithmetic is, and for the same reason.
        languageIndependentStimulus: false,
    },
    measurement: {
        scoring: "accuracy",
        /**
         * Two, because the bottom of the ladder is a different board.
         *
         * Levels one and two are a 3×3 now rather than a 4×4, and every level above them shifted to
         * make room. "Level 4" therefore names a different puzzle than it did, which is the precise
         * case this field exists for (v2 §18): a child's old level is not readable against the new
         * ladder, and the two must never be pooled.
         */
        calibrationVersion: "2",
        formIds: ["A", "B"],
        minDifficulty: 1,
        maxDifficulty: 10,
        stepUpAfter: 2,
        ...TEACH,
        // A whole grid, unhurried, and slower than anything else here.
        trialTimeoutMs: 240_000,
    },
    params: {},
};
/**
 * Smallest to biggest — tap the items in order of size.
 *
 * Seriation, a classic developmental task for exactly this age band, and the only game where
 * the child imposes an ORDER rather than picking, placing or repeating.
 *
 * **Difficulty is how close the sizes are, not how many there are.** Extended to eight levels
 * along that axis: items go from three to seven, and the smallest gap between neighbours
 * narrows from three tenths of the range to six hundredths. The gap does the work — adding
 * items mostly adds scanning, and narrowing the gaps is what the developmental literature
 * varies. Seven items is the ceiling because eight on a phone row leaves each one too small to
 * compare by eye, which would make it a game about screen resolution.
 */
export const vsOrdering = {
    id: "vs-ordering-bare",
    version: "2.0.0",
    construct: "visuospatial",
    family: "vs-ordering",
    presentation: {
        narrative: false,
        theme: "plain-shapes",
        locale: "fa-IR",
        languageIndependentStimulus: true,
    },
    measurement: {
        scoring: "accuracy",
        calibrationVersion: "2",
        formIds: ["A", "B"],
        minDifficulty: 1,
        maxDifficulty: 8,
        stepUpAfter: 3,
        ...TEACH,
        trialTimeoutMs: 60_000,
    },
    params: {},
};
// ---------------------------------------------------------------------------------------
// Space
// ---------------------------------------------------------------------------------------
/**
 * Shadow matching — which animal cast this shadow?
 *
 * The one activity here that removes every cue but the OUTLINE. Colour, markings and face are gone
 * from the thing being matched, so a child has to hold a silhouette against a coloured drawing and
 * decide whether they are the same animal. Nothing else in the bundle asks that: matching a pattern
 * compares two things drawn identically, and spot-the-difference compares two things that are
 * almost identical.
 *
 * **The stimulus is real artwork rather than shapes this codebase invented**, and the difference is
 * not decoration. Twenty-four animals in eight families of three, where a family shares an outline
 * and differs only in the detail a silhouette removes — a horse, a donkey and a zebra are the same
 * shadow. That similarity is a fact about animals rather than an assertion in a table, which is
 * what makes the hard levels honestly hard; the hand-drawn primitives this game shipped with first
 * were similar only because a comment said so.
 *
 * **Difficulty is the company the answer keeps, never the shadow itself.** A silhouette is already
 * the hardest version of a shape; shrinking or turning it would add mental rotation, which is a
 * different construct and would make the top of this ladder measure something the bottom does not.
 * So levels 1–3 add cards, 4–5 fill them with the answer's own look-alikes, and 6–8 do both.
 *
 * Six options is the ceiling, and it is the phone rather than the task: the shared choice renderer
 * derives its columns from the touch floor, so six land as 3×2 at roughly 100 px and eight would
 * land as 4×2 at 76 — exactly on the floor, with nothing left for a mis-aimed finger.
 */
export const vsShadow = {
    id: "vs-shadow-bare",
    version: "1.0.0",
    construct: "visuospatial",
    family: "vs-shadow",
    presentation: {
        narrative: false,
        theme: "plain-shapes",
        locale: "fa-IR",
        languageIndependentStimulus: true,
    },
    measurement: {
        scoring: "accuracy",
        calibrationVersion: "1",
        formIds: ["A", "B"],
        minDifficulty: 1,
        maxDifficulty: 8,
        stepUpAfter: 3,
        ...TEACH,
        // Comparing an outline against a drawing is looking rather than working, and PC-1 keeps every
        // timing signal out of scoring in any case.
        trialTimeoutMs: 30_000,
    },
    params: {},
};
/**
 * Odd one out — three identical shapes and one mirrored.
 *
 * The difference is a REFLECTION, which makes it a spatial discrimination — the same left/right
 * distinction behind b/d confusion, and worth practising for exactly that reason in the year
 * before reading. Mirroring preserves the cell count, so counting is not a shortcut.
 *
 * Difficulty is filled cells: more cells make the reflection subtler. The generator already
 * accepted up to eight, so the ceiling here was under its own tooling rather than at a limit.
 */
export const vsOddOneOut = {
    id: "vs-odd-one-out-bare",
    version: "2.0.0",
    construct: "visuospatial",
    family: "vs-odd-one-out",
    presentation: {
        narrative: false,
        theme: "plain-shapes",
        locale: "fa-IR",
        languageIndependentStimulus: true,
    },
    measurement: {
        scoring: "accuracy",
        calibrationVersion: "1",
        formIds: ["A", "B"],
        minDifficulty: 2,
        maxDifficulty: 8,
        stepUpAfter: 3,
        ...TEACH,
        trialTimeoutMs: 30_000,
    },
    params: {
        gridCols: 3,
        gridRows: 3,
        options: 4,
    },
};
/**
 * Build the same shape — reproduce the target on an empty grid.
 *
 * The target stays visible while the child builds. That is what makes it construction rather
 * than memory, and it is the point: it is the one task where a child MAKES something instead
 * of choosing or repeating.
 *
 * `difficulty` is cells to place, on a 3×3 grid, so eight is the arithmetic ceiling — nine
 * cells is the whole grid filled and there is nothing to get wrong.
 */
export const vsBuildCopy = {
    id: "vs-build-copy-bare",
    version: "2.0.0",
    construct: "visuospatial",
    family: "vs-build-copy",
    presentation: {
        narrative: false,
        theme: "plain-shapes",
        locale: "fa-IR",
        languageIndependentStimulus: true,
    },
    measurement: {
        scoring: "accuracy",
        calibrationVersion: "1",
        formIds: ["A", "B"],
        minDifficulty: 2,
        maxDifficulty: 8,
        stepUpAfter: 2,
        ...TEACH,
        // Generous: building is self-paced, and a child arranging carefully must not be timed out
        // for doing the task properly.
        trialTimeoutMs: 60_000,
    },
    params: {
        gridCols: 3,
        gridRows: 3,
    },
};
/**
 * Which pieces make this? — taking a shape apart in your head.
 *
 * A thing built from coloured blocks above, piles of loose pieces below, and the child picks the
 * pile it is made of. The one activity here that runs from the whole to the parts: everything else
 * compares a shape with a shape, and this asks for an inventory of something that is not laid out
 * as an inventory.
 *
 * **The pieces in an option are never shown in place**, because an option arranged like the figure
 * would be answerable by laying two pictures on top of each other, which is a task about eyesight.
 * Scattered, the only route is to decompose the whole and check kinds and colours against the pile.
 *
 * Difficulty alternates between two axes — how many pieces the figure takes, and how subtle the
 * wrong piles are — because raising either one alone stops producing new difficulty quickly. Four
 * pieces is the ceiling: an option card on a phone is about 100 px, and five pieces scattered
 * inside it stop being separately readable, which would make the top level a test of screen
 * resolution.
 */
export const vsParts = {
    id: "vs-parts-bare",
    version: "1.0.0",
    construct: "visuospatial",
    family: "vs-parts",
    presentation: {
        narrative: false,
        theme: "plain-shapes",
        locale: "fa-IR",
        languageIndependentStimulus: true,
    },
    measurement: {
        scoring: "accuracy",
        calibrationVersion: "1",
        formIds: ["A", "B"],
        minDifficulty: 1,
        maxDifficulty: 8,
        stepUpAfter: 3,
        ...TEACH,
        // Counting an inventory is unhurried, and longer than a straight comparison: the child has to
        // check every pile rather than recognise one.
        trialTimeoutMs: 45_000,
    },
    params: {},
};
/**
 * The butterfly's other wing — one wing is painted, and the child paints its reflection.
 *
 * The production half of symmetry, and the counterpart of `vs-odd-one-out`: that game asks a child
 * to RECOGNISE a reflection among four cards, and this one asks them to make one. A preschool
 * teaches symmetry with a fold and a paint blot, and nothing else in the bundle asks a child to
 * produce a transformed shape at all.
 *
 * **This replaced a version that was the copying game wearing a different name**, and each thing
 * that changed was a thing that was wrong.
 *
 * *It looked identical to `vs-build-copy`.* Same renderer, same stars, two stacked panels; only the
 * right answer differed. A child could not tell which game they had opened, and neither could a
 * teacher watching — and a game whose only distinguishing feature is invisible will be played as
 * the other one.
 *
 * *The fold ran top-to-bottom*, which is the axis that matters least. The reflection worth
 * practising in the year before reading is left-right — it is the b/d distinction — and the old
 * version said so and chose the other axis anyway, because two three-column panels side by side put
 * every cell under the size a finger lands on. The answer is fewer columns, not the wrong axis.
 *
 * *And there was no line*, though "fold along the line" is the whole idea. Here the line is the
 * butterfly's body, which is a line nobody has to explain, and the two wings are already the two
 * halves of the thing being taught.
 *
 * **Colour is a second axis and it makes the top of the ladder a different question.** With one
 * colour the child mirrors positions; with three, a spot has to arrive in the right place AND the
 * right colour, and those can be got wrong independently.
 *
 * **A separate family from copying, and their scores must never be pooled.** Copying a shape and
 * reflecting one are different tasks and a child who can do the first frequently cannot do the
 * second — averaging them would report a number describing neither, which is the mistake the
 * forward/backward span split exists to prevent.
 */
export const vsMirror = {
    id: "vs-mirror-bare",
    version: "2.0.0",
    construct: "visuospatial",
    family: "vs-mirror",
    presentation: {
        narrative: false,
        theme: "plain-shapes",
        locale: "fa-IR",
        languageIndependentStimulus: true,
    },
    measurement: {
        scoring: "accuracy",
        /**
         * Two, because this is a different game under the same name.
         *
         * The first version was the copying game with a transformed answer: two stacked grids of stars,
         * a top-to-bottom fold, and no line anywhere. It has been replaced by a butterfly whose body IS
         * the fold, a left-right reflection, and colour as a second axis — so a level number means
         * something else than it did, and a trial recorded under calibration 1 and one recorded under 2
         * are not the same quantity (v2 §18).
         */
        calibrationVersion: "2",
        formIds: ["A", "B"],
        minDifficulty: 1,
        maxDifficulty: 8,
        stepUpAfter: 2,
        ...TEACH,
        // Painting a wing is unhurried, and a child checking each spot against the one it mirrors is
        // doing the task properly rather than slowly (PC-1).
        trialTimeoutMs: 120_000,
    },
    params: {
        /**
         * Two spot columns per WING, three rows.
         *
         * Two is not a shortage, it is the screen. Four spot columns and a body have to fit across a
         * 360 px phone, which puts a spot at about 76 px — the size at which a five-year-old's finger
         * lands where they aimed. At three columns a spot falls near 50 px, and then every mis-aimed
         * tap is recorded as not understanding symmetry.
         *
         * Two columns is still a real reflection: the spot nearest the body has to come back nearest
         * the body, and a child who slides the pattern across instead gets both columns wrong. That is
         * the error the game is for, and two columns is all it takes to catch it.
         */
        wingCols: 2,
        wingRows: 3,
    },
};
/**
 * Magic maze — find the way out.
 *
 * `difficulty` is cells per side, and it now runs to eight rather than six. The old ceiling
 * carried a comment about cells falling under the touch floor, which was true of the FIRST
 * design and stopped being true when the controls became a D-pad: the child presses four fixed
 * arrows, so a maze cell only has to be visible, not hittable. The renderer says so at
 * `maze-task.ts`; the ceiling had simply not been revisited.
 *
 * Almost every child reaches the exit; what differs is steps against the shortest route and how
 * often they double back, which is the difference between looking before moving and trying
 * every corridor. Both are recorded per trial.
 */
export const vsMaze = {
    id: "vs-maze-bare",
    version: "2.0.0",
    construct: "visuospatial",
    family: "vs-maze",
    presentation: {
        narrative: false,
        theme: "plain-shapes",
        locale: "fa-IR",
        languageIndependentStimulus: true,
    },
    measurement: {
        scoring: "accuracy",
        calibrationVersion: "2",
        formIds: ["A", "B"],
        minDifficulty: 3,
        maxDifficulty: 8,
        stepUpAfter: 2,
        ...TEACH,
        // Self-paced and unhurried: a child working out a route must not be timed out for doing
        // the task properly. PC-1 means nothing here is a speed measure.
        trialTimeoutMs: 120_000,
    },
    params: {},
};
/**
 * Robot engineer — write the program, then run it.
 *
 * The only activity where the answer is committed before any feedback arrives: the child queues
 * commands and presses run, and nothing can be corrected mid-move. That is what makes **how
 * many runs a trial took** the most informative thing it records — a child who plans runs once,
 * a child who guesses runs three times and adjusts, and both may reach the goal.
 *
 * Difficulty grows the grid, then the obstacles, then the program length, so each level
 * introduces one new thing. Extended to eight; the child taps command buttons rather than
 * cells, so the grid can shrink past the touch floor without costing anyone a mis-tap.
 */
export const vsRobot = {
    id: "vs-robot-bare",
    version: "2.0.0",
    construct: "reasoning",
    family: "vs-robot",
    presentation: {
        narrative: false,
        theme: "plain-shapes",
        locale: "fa-IR",
        languageIndependentStimulus: true,
    },
    measurement: {
        scoring: "accuracy",
        calibrationVersion: "2",
        formIds: ["A", "B"],
        minDifficulty: 1,
        maxDifficulty: 8,
        stepUpAfter: 2,
        ...TEACH,
        // Writing a program is unhurried by nature, and PC-1 means nothing here is a speed measure.
        trialTimeoutMs: 180_000,
    },
    params: {},
};
// ---------------------------------------------------------------------------------------
// Memory
// ---------------------------------------------------------------------------------------
/** Shared between the two span presentations — identical by construction (the A-13 arms). */
const wmSpanMeasurement = {
    scoring: "span",
    /**
     * Bumped to "3" for the ninth level and the teaching policy.
     *
     * `calibrationVersion` exists so that a cosmetic change can bump the task version while
     * comparability survives, and a change to how difficulty behaves cannot (v2 §18). Errors no
     * longer end the task, so a span recorded under the old rule and one recorded under this one
     * are not the same quantity: the old one stopped near the child's limit and this one keeps
     * going. Pooling them would report a span describing neither.
     */
    calibrationVersion: "3",
    // Three forms so repeated play cannot conflate familiarity with growth.
    formIds: ["A", "B", "C"],
    minDifficulty: 2,
    maxDifficulty: 9,
    stepUpAfter: 2,
    ...TEACH,
    trialTimeoutMs: 20_000,
};
const wmSpanParams = {
    gridCols: 3,
    gridRows: 3,
    /** Per-item presentation time. Generous on purpose — this is a span task, not a speed
     *  task, and slow devices must not change what is being practised (PC-1). */
    itemOnMs: 900,
    itemGapMs: 400,
    /** Beat before recall begins, so the child is not rushed into responding. */
    recallDelayMs: 500,
};
export const wmSpanBare = {
    id: "wm-span-forward-bare",
    version: "2.0.0",
    construct: "working_memory",
    family: "wm-span-forward",
    presentation: {
        narrative: false,
        theme: "plain-tiles",
        locale: "fa-IR",
        languageIndependentStimulus: true,
    },
    measurement: wmSpanMeasurement,
    params: wmSpanParams,
};
/**
 * The same task inside a story.
 *
 * The two arms are scoring-identical and differ only in whether a narrative wrapper is present
 * (A-13). They stayed through the pivot because the question they answer is now a product
 * question rather than a validity one: whether a child plays longer when the lanterns are
 * fireflies is worth knowing for its own sake, and it is still a config difference rather than
 * a branch in code.
 */
export const wmSpanStory = {
    id: "wm-span-forward-story",
    version: "2.0.0",
    construct: "working_memory",
    family: "wm-span-forward",
    presentation: {
        narrative: true,
        theme: "fireflies",
        locale: "fa-IR",
        languageIndependentStimulus: true,
    },
    measurement: wmSpanMeasurement,
    params: wmSpanParams,
};
/**
 * Backward span — the sequence, tapped in reverse.
 *
 * A separate family from forward span, not a difficulty setting, because holding a sequence AND
 * turning it around is a heavier load than holding it. Their spans must never be pooled:
 * backward spans run systematically shorter, so averaging the two would report a number that
 * describes neither.
 *
 * A lower ceiling than forward span for the same reason.
 */
export const wmSpanBackward = {
    id: "wm-span-backward-bare",
    version: "2.0.0",
    construct: "working_memory",
    family: "wm-span-backward",
    presentation: {
        narrative: false,
        theme: "plain-tiles",
        locale: "fa-IR",
        languageIndependentStimulus: true,
    },
    measurement: {
        scoring: "span",
        calibrationVersion: "2",
        formIds: ["A", "B", "C"],
        minDifficulty: 2,
        maxDifficulty: 7,
        stepUpAfter: 2,
        ...TEACH,
        trialTimeoutMs: 25_000,
    },
    params: {
        gridCols: 3,
        gridRows: 3,
        itemOnMs: 900,
        itemGapMs: 400,
        // A longer beat than forward recall: the child has to turn the sequence around before they
        // can start, and rushing that would practise haste.
        recallDelayMs: 900,
        recallDirection: "backward",
    },
};
/**
 * Hidden pairs — turn two cards over and find the match.
 *
 * The most recognisable children's game there is, and a working-memory task without pretending
 * to be one: every card turned over and not matched is something to hold on to.
 *
 * **Difficulty grows on two axes, because one ran out.** Three columns is the practical maximum
 * on a phone — at four, a card lands around 76 px, and a mis-tap here does not merely cost a
 * point, it reveals a card the child did not choose to reveal. So levels 1–5 and 7 add pairs up
 * to the widest board that fits, and levels 6 and 8 keep the board and make the CARDS harder to
 * tell apart: same shape throughout, differing only in colour. That is a genuine increase in
 * memory load — "the blue circle was here" is harder to hold than "the boat was here" — and it
 * costs no screen space at all.
 */
export const wmPairs = {
    id: "wm-pairs-bare",
    version: "2.0.0",
    construct: "working_memory",
    family: "wm-pairs",
    presentation: {
        narrative: false,
        theme: "plain-shapes",
        locale: "fa-IR",
        languageIndependentStimulus: true,
    },
    measurement: {
        scoring: "accuracy",
        calibrationVersion: "2",
        formIds: ["A", "B"],
        minDifficulty: 1,
        maxDifficulty: 8,
        stepUpAfter: 2,
        ...TEACH,
        // Clearing a board is unhurried, and nothing here is a speed measure (PC-1).
        trialTimeoutMs: 180_000,
    },
    params: {},
};
// ---------------------------------------------------------------------------------------
// Focus
// ---------------------------------------------------------------------------------------
/**
 * Spot the difference — two panels, a few cells changed.
 *
 * Visual attention and change detection, and the one activity where the interesting behaviour
 * is SEARCH: wrong taps on the way to the answer separate a child who scanned systematically
 * from one who guessed.
 *
 * **The grid is 3×2 and stays that way.** Two panels have to fit one phone screen with cells a
 * child can hit: stacked at 3×2 they come out around 108 px, comfortably past the floor; side
 * by side they would be 56 px, and at 3×3 stacked the second panel falls off the bottom.
 *
 * So the extra levels come from the KIND of change, not the count. Levels 1–5 change shapes and
 * then colours; 6–8 add SWAPS — two cells exchange their contents, so both panels hold exactly
 * the same six things and nothing can be found by noticing something new. A swap is the
 * subtlest difference there is on a fixed grid, and it needs no extra cells.
 */
export const vsDifference = {
    id: "vs-difference-bare",
    version: "2.0.0",
    construct: "visuospatial",
    family: "vs-difference",
    presentation: {
        narrative: false,
        theme: "plain-shapes",
        locale: "fa-IR",
        languageIndependentStimulus: true,
    },
    measurement: {
        scoring: "accuracy",
        calibrationVersion: "2",
        formIds: ["A", "B"],
        minDifficulty: 1,
        maxDifficulty: 8,
        stepUpAfter: 2,
        ...TEACH,
        // Comparing two panels is unhurried by nature, and PC-1 means nothing here is a speed
        // measure.
        trialTimeoutMs: 90_000,
    },
    params: {},
};
/**
 * Find them all — every arrow pointing the way the sign does.
 *
 * The first activity here that is not finished when the child finds something. A field of arrows,
 * a sign above it, and the trial ends only when every matching arrow has been tapped — so the
 * behaviour it records is COVERAGE, which nothing else in the bundle can see. Spot-the-difference
 * is the nearest relative and compares two small panels rather than sweeping one large one.
 *
 * **Arrows rather than pictures, because a direction has no name.** A field of apples and fish is
 * searched by remembering a word, which is a verbal strategy wearing a spatial task's clothes. An
 * arrow can only be held as an orientation and checked against the sign, which is what makes the
 * eight-direction levels harder rather than merely busier.
 *
 * **Four columns, ever.** Four cells across a 360 px phone are about 77 px, which is the floor
 * where a five-year-old's finger lands where they aimed — and here a mis-tap costs more than a
 * point, because the wrong-tap budget is what ends the trial. Difficulty grows down the screen,
 * then through the directions offered, and finally by drawing the wrong arrows from the two a
 * quarter-turn away.
 */
export const vsSearch = {
    id: "vs-search-bare",
    version: "1.0.0",
    construct: "visuospatial",
    family: "vs-search",
    presentation: {
        narrative: false,
        theme: "plain-shapes",
        locale: "fa-IR",
        languageIndependentStimulus: true,
    },
    measurement: {
        scoring: "accuracy",
        calibrationVersion: "1",
        formIds: ["A", "B"],
        minDifficulty: 1,
        maxDifficulty: 8,
        /**
         * Three, not the shared two, because this ladder's steepest step is the one a fast child
         * reaches soonest.
         *
         * At `stepUpAfter: 2` a child who answers cleanly climbs five levels in twelve trials (see
         * `TEACH`) — enough to go from level 1 straight through level 6, where the directions on offer
         * double from four to eight and an arrow can be wrong by a quarter turn instead of a half
         * (`search.ts`). That is the one place on this ladder the task's own comments call out as
         * genuinely harder rather than merely busier, and two clean answers was not enough practice at
         * levels 4 and 5 before a child was asked to do it. Three slows every level by the same amount
         * rather than singling out level 6, because the levels below it are what build the scanning
         * habit the harder ones then load.
         */
        stepUpAfter: 3,
        ...TEACH,
        // Sweeping a field is unhurried by nature, and a child who checks every cell twice is doing the
        // task properly rather than slowly. PC-1 keeps timing out of scoring regardless.
        trialTimeoutMs: 120_000,
    },
    params: {},
};
/**
 * Colour by the key — the legend says which colour each thing gets.
 *
 * **The only activity here where the rule is given rather than found.** Every other game asks a
 * child to work out what is going on; this one prints the rule at the top of the screen and asks
 * them to hold it and carry it out accurately six times running. That is a real and separate thing
 * — a preschool teacher would call it following instructions — and until now nothing in the bundle
 * asked for it.
 *
 * `ef-rule-switch` is the nearest relative and they are opposites: there the rule is hidden and has
 * to be inferred from feedback, here it is visible and the whole difficulty is in the applying. A
 * child can be good at one and hopeless at the other, so they are separate families.
 *
 * **Two kinds of key, and the second restarts easy.** Levels 1–5 key on shape, 6–8 on how many. The
 * count levels open with a two-entry key even though the shape levels closed with a four-entry one,
 * which is the argument sudoku makes when it swaps shapes for digits: a child meeting a new kind of
 * key is not where they were with the old one, and pretending otherwise would record a counting
 * problem as a collapse in a skill they still have.
 *
 * Six tiles is the ceiling on the board. Three columns on a 360 px phone give tiles around 106 px,
 * and every tile here is tapped several times.
 */
export const vsCodeKey = {
    id: "vs-code-key-bare",
    version: "1.0.0",
    construct: "reasoning",
    family: "vs-code-key",
    presentation: {
        narrative: false,
        theme: "plain-shapes",
        locale: "fa-IR",
        languageIndependentStimulus: true,
    },
    measurement: {
        scoring: "accuracy",
        calibrationVersion: "1",
        formIds: ["A", "B"],
        minDifficulty: 1,
        maxDifficulty: 8,
        stepUpAfter: 2,
        ...TEACH,
        // A whole board, unhurried. Nothing here is a speed measure (PC-1), and a child checking the
        // legend before every tile is doing the task properly rather than slowly.
        trialTimeoutMs: 150_000,
    },
    params: {},
};
/**
 * The rule changed — sort by shape, then suddenly by colour.
 *
 * The only game here where the ground moves under the child: a rule that was right becomes
 * wrong with no announcement, and they have to notice from the feedback and switch.
 *
 * **What is recorded is a count of actions, never a latency and never a trait.** The textbook
 * version of this task is timed and that version is unbuildable here — PC-1 excludes
 * reaction-time constructs because timing variance across the device fleet swamps the signal.
 * What this records is how many cards after a switch were still sorted by the old rule.
 *
 * Extended to eight levels through more cards, more switches and shorter runs between them.
 * The bins are large fixed targets, so nothing here approaches the touch floor.
 *
 * **`calibrationVersion` is 3 because the levels were not delivering what they promised.** The
 * cards were drawn independently of the rules, and with only two legal cards the correct bin is
 * the two of them combined — so whenever the drawn card happened to turn over at the same index
 * the rule did, the two cancelled and the right answer was the bin the child had just pressed.
 * Two thirds of every switch in the game changed nothing observable, and at level one, which has
 * a single switch, sixty-nine percent of rounds contained no observable rule change at all.
 *
 * A level now delivers roughly three times the switches it used to, so a round played under
 * calibration 2 and one played under 3 are not the same quantity and must not be pooled. That is
 * exactly what the field is for (v2 §18).
 */
export const efRuleSwitch = {
    id: "ef-rule-switch-bare",
    version: "2.2.0",
    construct: "reasoning",
    family: "ef-rule-switch",
    presentation: {
        narrative: false,
        theme: "plain-shapes",
        locale: "fa-IR",
        languageIndependentStimulus: true,
    },
    measurement: {
        scoring: "accuracy",
        /**
         * Four, because every level is denser than the level of the same name was.
         *
         * The recipes each shed the cards they were carrying above what their switches and runs require
         * — four of them at the top five levels — so a level's number now describes a round with the
         * rule changing more often per card than before. Higher density is a different quantity, not a
         * harder version of the same one, and pooling a score from either side of this would report an
         * accuracy describing neither (v2 §18).
         */
        calibrationVersion: "4",
        formIds: ["A", "B"],
        minDifficulty: 1,
        maxDifficulty: 8,
        stepUpAfter: 2,
        ...TEACH,
        /**
         * Eight trials, not the shared twelve, for the reason `ef-key-map-bare` has eight: a trial here
         * is a whole ROUND.
         *
         * This was the worse of the two. `TEACH` budgets about five minutes, and rounds of up to
         * twenty-two cards over twelve trials meant a child resuming at the top of the ladder was asked
         * for **two hundred and sixty-four cards** in a sitting — a preschool activity three or four
         * times past the length its own policy calls the point of no return.
         *
         * Eight rounds also happens to be exactly one objective window here, since this family's windows
         * are four levels wide (`objectives-2.0.0.ts`) and `stepUpAfter: 2` climbs four levels in eight
         * trials. The sitting got shorter and the ladder did not get slower — the same trade the sibling
         * task makes.
         */
        maxTrials: 8,
        // A whole round of cards, unhurried. Nothing here is a speed measure (PC-1).
        trialTimeoutMs: 120_000,
    },
    params: {},
};
/**
 * Drive the car — the key says which way each colour goes, and the key changes.
 *
 * **The other half of `ef-rule-switch`, and the pair is the point.** There the rule is hidden and
 * the task is inferring from feedback that it moved. Here the change happens in plain sight, at the
 * top of the screen, in a colour the child can see — so answering by the old key is not a failure
 * to work anything out. It is a HABIT: six cards of red-goes-right builds a hand that goes right,
 * and the work is stopping it.
 *
 * A child can be good at one and poor at the other, which is why they are separate families. What
 * this one records is the same kind of thing and not the same thing: **cards after a key change
 * that were answered by the key it replaced.** A count of actions, never a latency — PC-1 excludes
 * reaction-time constructs because timing variance across the device fleet swamps the signal.
 *
 * The first level runs on two colours, which is two directions and two buttons. Barely a key, and
 * right for a first level for the reason the search game opens on six arrows: the thing being learnt
 * is that the strip at the top can change, and learning that should not also require holding four
 * mappings at once. It is six cards and there is only one of it — see `key-map.ts` for why it used
 * to be two levels and why that was the thing children left over.
 */
export const efKeyMap = {
    id: "ef-key-map-bare",
    version: "2.1.0",
    construct: "reasoning",
    family: "ef-key-map",
    presentation: {
        narrative: false,
        theme: "plain-shapes",
        locale: "fa-IR",
        languageIndependentStimulus: true,
    },
    measurement: {
        scoring: "accuracy",
        /**
         * Three, because every level below the top two now describes a different round.
         *
         * Two was the run-length fix: the old ladder allowed runs of two cards, which at three colours
         * meant the key changed before every colour had come round once, so a level's number described a
         * round nobody could use the key in. Three is the ladder losing its duplicate two-colour level
         * and the 4-colour tier being re-spaced, plus a two-colour deck that no longer strictly
         * alternates. Levels 1–6 are not the levels of the same name in v2, so a score recorded under one
         * is not the same quantity as a score recorded under the other (v2 §18).
         */
        calibrationVersion: "3",
        formIds: ["A", "B"],
        minDifficulty: 1,
        maxDifficulty: 8,
        stepUpAfter: 2,
        ...TEACH,
        /**
         * Eight trials, not the shared twelve, because here a trial is a whole ROUND.
         *
         * `TEACH` picks twelve on an explicit budget: about five minutes, after which a preschooler
         * leaves rather than finishes. That arithmetic holds for the other tasks, where a trial is one
         * item and twelve of them is twelve answers. A round here is six to thirteen cards, each with its
         * own look-up, press and drive-off — so twelve rounds is a hundred and ten cards on a clean climb
         * from the bottom, and a hundred and forty resuming in the middle of the ladder. Well past the
         * budget, and it was reached the way over-runs usually are: by inheriting a number whose
         * justification did not survive the change in what a trial is.
         *
         * Eight rounds is sixty-six cards from the bottom, comfortably inside five minutes, and still
         * four level-pairs at `stepUpAfter: 2` — so one sitting clears a whole objective window (they are
         * three levels wide) exactly as before. The sitting got shorter; the ladder did not get slower.
         */
        maxTrials: 8,
        // A whole round of cards, unhurried. Nothing here is a speed measure (PC-1) — which matters
        // more in this game than in most, because the textbook version of it is timed and that version
        // is unbuildable on this fleet.
        trialTimeoutMs: 150_000,
    },
    params: {},
};
/** Every task in bundle 2.0.0, in the order the child's shelf shows them. */
export const ALL_TASKS = [
    mathArith,
    litLetters,
    litFindLetter,
    logicSudoku,
    vsSequence,
    vsSequenceColor,
    vsTiling,
    vsPatternMatch,
    vsOrdering,
    vsShadow,
    vsOddOneOut,
    vsParts,
    vsBuildCopy,
    vsMirror,
    vsMaze,
    vsRobot,
    wmSpanBare,
    wmSpanStory,
    wmSpanBackward,
    wmPairs,
    vsDifference,
    vsSearch,
    vsCodeKey,
    efRuleSwitch,
    efKeyMap,
];
//# sourceMappingURL=task-defs.js.map